import camelcaseKeys from 'camelcase-keys';
import { defineStore } from 'pinia';

import OpportunitiesAPI from 'dashboard/api/opportunities';
import PipelinesAPI from 'dashboard/api/pipelines';
import { throwErrorMessage } from 'dashboard/store/utils/api';

/**
 * Deals — the opportunity pipeline.
 *
 * State shape
 *   pipelines          the account's active boards, each carrying its stages in
 *                      position order (the server orders them; nothing re-sorts)
 *   activePipelineId   the board on screen; one pipeline at a time, because the
 *                      columns are that pipeline's stages
 *   records / listIds  the deals of the active pipeline, normalised by id and in
 *                      the order the server sent them
 *
 * `GET /opportunities` answers one flat array for a pipeline — no pages, no
 * per-stage endpoint — so the board's columns are a client-side grouping of that
 * one response by `pipeline_stage_id`, and there is no "load more" to own.
 *
 * The single behavioral seam stays on the server: a drop calls `change_stage`,
 * which is where won_at/lost_at and the customer promotion are written. This
 * store only owns the optimistic move and its rollback.
 */

// `custom_attributes` is a map whose keys are the account's own attribute
// definition keys, not a record — camelising them would rewrite the data
// (`order_id` -> `orderId`). One record at a time because `stopPaths` is rooted
// at the object it is handed, not at an array of them.
const camelize = record =>
  camelcaseKeys(record, { deep: true, stopPaths: ['custom_attributes'] });

export const useOpportunitiesStore = defineStore('opportunities', {
  state: () => ({
    pipelines: [],
    records: {},
    listIds: [],
    activePipelineId: null,
    uiFlags: {
      isFetchingPipelines: false,
      isFetchingList: false,
    },
  }),

  getters: {
    activePipeline: state =>
      state.pipelines.find(
        pipeline => pipeline.id === state.activePipelineId
      ) || null,

    opportunities: state =>
      state.listIds.map(id => state.records[id]).filter(Boolean),

    // One column per active stage, in the pipeline's own order. A stage with no
    // deals is still a column: it is where the next drop lands.
    boardColumns() {
      return (this.activePipeline?.stages || []).map(stage => ({
        key: stage.id,
        stage,
        opportunities: this.opportunities.filter(
          opportunity => opportunity.pipelineStageId === stage.id
        ),
      }));
    },
  },

  actions: {
    upsert(record) {
      if (!record?.id) return null;
      this.records[record.id] = { ...this.records[record.id], ...record };
      return this.records[record.id];
    },

    // The board opens on the account's default pipeline; an account whose admin
    // has not marked one falls back to the first, which is the only other honest
    // answer to "which board".
    async fetchPipelines() {
      this.uiFlags.isFetchingPipelines = true;
      try {
        const { data } = await PipelinesAPI.get();
        this.pipelines = data.map(camelize);
        if (!this.activePipeline) {
          this.activePipelineId =
            (
              this.pipelines.find(pipeline => pipeline.isDefault) ||
              this.pipelines[0]
            )?.id ?? null;
        }
        return this.pipelines;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.uiFlags.isFetchingPipelines = false;
      }
    },

    setActivePipeline(pipelineId) {
      this.activePipelineId = pipelineId;
    },

    async fetchOpportunities() {
      if (!this.activePipelineId) return [];

      this.uiFlags.isFetchingList = true;
      try {
        const { data } = await OpportunitiesAPI.get({
          pipeline_id: this.activePipelineId,
        });
        const records = data.map(camelize);
        records.forEach(record => this.upsert(record));
        this.listIds = records.map(record => record.id);
        return this.opportunities;
      } catch (error) {
        // Drop the previous board rather than showing another pipeline's deals
        // under this one's columns.
        this.listIds = [];
        return throwErrorMessage(error);
      } finally {
        this.uiFlags.isFetchingList = false;
      }
    },

    // The drag. Optimistic on the card's stage with a snapshot rollback: a deal
    // that could not move must land back in the column it left rather than
    // sitting in one the server does not agree with. The response carries the
    // won/lost timestamps the service wrote, so success replaces the record
    // outright.
    async moveToStage(opportunityId, stage) {
      const record = this.records[opportunityId];
      if (!record || !stage?.id || record.pipelineStageId === stage.id) {
        return record;
      }

      const previousStageId = record.pipelineStageId;
      record.pipelineStageId = stage.id;

      try {
        const { data } = await OpportunitiesAPI.changeStage(
          opportunityId,
          stage.id
        );
        return this.upsert(camelize(data));
      } catch (error) {
        record.pipelineStageId = previousStageId;
        return throwErrorMessage(error);
      }
    },
  },
});
